// INCREMENTAL SCORING MODEL for the refine's trial loop (TGCLOUD-1909, step three).
//
// crossingRefine's measure() needs a render model only to count crossings, and the
// detector (lineIntersection.js) reads just: each node's x, y and scoreLaneY; each
// spouse edge's endpoints and lane fields (laneY, scoreLaneY); each child edge's
// endpoints (its de-merge lane is a drawing tag the count ignores); and a twin-group
// edge's union and twin ids. buildRenderModel(pf, cp, true) rebuilt all of that from
// scratch on every trial — new node objects, the union loop, the lane index, the
// sibling-line de-merge — about 0.3 ms a trial, a third of the loop once the crossing
// count was incremental.
//
// This builder keeps ONE model per refine run and brings it up to date per call: person
// nodes keep their objects and take their positions in place; union nodes are placed
// again (partner midpoint, extra-spouse step or asym band, exactly as buildRenderModel
// places them) and spouse edges take their lane fields again, since both depend on
// where everyone now stands; child and twin-group edges are structural and stay. The
// de-merge, the reproductive satellites, adoption parking and the displaced-sibship
// lift are skipped: none of them changes a count (the first adds tags, the rest never
// run in a scoring build).
//
// It mirrors buildRenderModel's scoring-mode geometry rule for rule, and must keep
// doing so — the G-1-01406 lesson is that a metric aimed where the renderer never draws
// makes the optimiser worse, not better. `VERIFY_INCREMENTAL_SCORING_MODEL` builds the
// full scoring model beside this one on every call and throws when the two counts
// differ; the corpus gate turns it on, and scoringModel.test.js compares the models
// field by field as well.

import { config } from './config.js';
import { yFromRowUnits } from './rowUnits.js';
import { xFromColUnits } from './colUnits.js';

// Mirrors layout.js createUnionId (a default sort: ids compared as strings).
const createUnionId = (id1, id2) => {
  const [first, second] = [id1, id2].sort();
  return `union-${first}-${second}`;
};
const bridgeKey = (a, b) => (a < b ? `${a}:${b}` : `${b}:${a}`);
const finite = (v) => (Number.isFinite(v) ? v : undefined);
const ROW_EPS = 1;

export function createScoringModelBuilder() {
  let state = null;

  const initialise = (pf, customPositions) => {
    const NODE = config.NODE_TYPE.NODE;
    const UNION = config.NODE_TYPE.UNION;
    const nodes = [];
    const nodesById = new Map();
    const persons = [];
    for (const person of pf.persons) {
      const node = {
        pid: person.pid,
        uid: person.uid,
        gender: person.gender,
        kind: NODE,
        x: person.x,
        y: person.y,
        virtualX: person.virtualX,
        virtualY: person.virtualY,
        rowDepth: person.rowDepth,
        motherId: person.motherId,
        fatherId: person.fatherId,
        spouseIds: person.spouseIds,
        childrenIds: person.childrenIds
      };
      nodes.push(node);
      nodesById.set(person.pid, node);
      persons.push({ person, node, pos: customPositions?.get(person.uid) });
    }
    // Hubs whose extra spouses step upward (3+ spouses; 2 with the lift flag —
    // the lifted set itself is read per call). Same dedupe as the builder: a
    // hub's spouses are never processed as hubs of their own.
    const hubs = [];
    const processed = new Set();
    for (const person of pf.persons) {
      const nSpouses = person.spouseIds.length;
      const allowTwo = config.ENABLE_UNFLANKED_SPOUSE_LIFT && nSpouses === 2;
      if (nSpouses < 3 && !allowTwo) continue;
      if (processed.has(person.pid)) continue;
      processed.add(person.pid);
      person.spouseIds.forEach((sid) => processed.add(sid));
      hubs.push({ person, nSpouses });
    }
    // Unions, in the builder's (person, spouse) order, the first pair naming the
    // union; the lane and the extra-spouse step read the first person's row.
    const unions = [];
    const unionById = new Map();
    for (const person of pf.persons) {
      if (person.spouses.length === 0 || person.children.length === 0) continue;
      for (const spouse of person.spouses) {
        const shared = person.children.filter(
          (c) => c.motherId === spouse.pid || c.fatherId === spouse.pid
        );
        if (shared.length === 0) continue;
        const unionId = createUnionId(person.pid, spouse.pid);
        if (unionById.has(unionId)) continue;
        const node = {
          pid: unionId,
          kind: UNION,
          x: 0,
          y: 0,
          parent1Id: person.pid,
          parent2Id: spouse.pid,
          childrenIds: shared.map((c) => c.pid),
          gender: 'union'
        };
        const u = {
          unionId,
          person,
          spouse,
          shared,
          node,
          key:
            person.pid < spouse.pid
              ? `${person.pid}:${spouse.pid}`
              : `${spouse.pid}:${person.pid}`
        };
        unions.push(u);
        unionById.set(unionId, u);
        nodes.push(node);
        nodesById.set(unionId, node);
      }
    }
    // Spouse edges, once per couple from the lower pid; the lane fields are per call.
    const edges = [];
    const spouseEdges = [];
    for (const person of pf.persons) {
      for (const spouse of person.spouses) {
        if (person.pid > spouse.pid) continue;
        const rel =
          person.data?.spouseRelationships?.[spouse.pid] ||
          spouse.data?.spouseRelationships?.[person.pid] ||
          {};
        const edge = {
          kind: 'spouse',
          from: person.pid,
          to: spouse.pid,
          parentRelationship: rel.parentRelationship ?? 'married',
          isConsanguineous: rel.isConsanguineous ?? false
        };
        edges.push(edge);
        spouseEdges.push({ edge, person, spouse });
      }
    }
    // Child and twin-group edges: structural.
    const processedTwins = new Set();
    for (const person of pf.persons) {
      if (!person.motherId && !person.fatherId) continue;
      if (processedTwins.has(person.pid)) continue;
      const mother = person.mother;
      const father = person.father;
      if (!mother && !father) continue;
      let fromId;
      if (mother && father) fromId = createUnionId(mother.pid, father.pid);
      else fromId = (mother || father).pid;
      if (!nodesById.has(fromId)) continue;
      const twins = pf.getTwinsInSameSubGroup(person);
      if (twins.length > 1) {
        const positioned = twins.filter((t) => nodesById.has(t.pid));
        if (positioned.length > 0) {
          edges.push({
            kind: 'twin-group',
            from: fromId,
            twinIds: positioned.map((t) => t.pid),
            cohortPids: pf
              .getSibshipOf(person)
              .map((p) => p.pid)
              .filter((pid) => nodesById.has(pid))
          });
          twins.forEach((t) => processedTwins.add(t.pid));
        }
      } else {
        const laneY = pf.overtopLaneByChild?.get(person.pid) ?? null;
        edges.push({
          kind: 'child',
          from: fromId,
          to: person.pid,
          ...(laneY != null ? { laneY } : {})
        });
      }
    }
    return {
      pf,
      customPositions,
      pinRows: pf.rowsForPins(),
      nodes,
      nodesById,
      edges,
      persons,
      hubs,
      unions,
      spouseEdges
    };
  };

  const update = (st) => {
    const { pf, customPositions, pinRows, nodesById, persons, hubs, unions } =
      st;
    const NODE_GAP = config.NODE_GAP;
    const rowOf = (p) => Math.round(p.virtualY);
    const pinX = (pos, row) =>
      pos == null
        ? undefined
        : finite(
            pos.gx != null ? xFromColUnits(pos.gx, null, NODE_GAP, row) : pos.x
          );
    const pinY = (pos) =>
      pos == null
        ? undefined
        : finite(pos.gy != null ? yFromRowUnits(pos.gy, pinRows) : pos.y);

    // 1. People take their laid-out (or pinned) positions.
    for (const { person, node, pos } of persons) {
      node.x = pinX(pos, rowOf(person)) ?? person.x;
      node.y = pinY(pos) ?? person.y;
      node.virtualX = person.virtualX;
      node.virtualY = person.virtualY;
    }

    // 2. Extra spouses step upward (by the hub's row gap), closest first per side.
    const extraSpouseInfo = new Map();
    const lifted = pf.liftedExtraSpouse2;
    for (const { person, nSpouses } of hubs) {
      const personNode = nodesById.get(person.pid);
      if (!personNode) continue;
      const spouseNodes = person.spouseIds
        .map((sid) => nodesById.get(sid))
        .filter(Boolean);
      const left = spouseNodes
        .filter((s) => s.x < personNode.x)
        .sort((a, b) => b.x - a.x);
      const right = spouseNodes
        .filter((s) => s.x >= personNode.x)
        .sort((a, b) => a.x - b.x);
      const step = 0.1 * pf.rowGapAbove(person.virtualY);
      const offsetSide = (side) => {
        for (let i = 1; i < side.length; i++) {
          const sp = side[i];
          if (nSpouses === 2 && !(lifted && lifted.has(sp.pid))) continue;
          if (!customPositions?.has(sp.uid)) sp.y -= step * i;
          extraSpouseInfo.set(sp.pid, { previousSpouseNode: side[i - 1] });
        }
      };
      offsetSide(left);
      offsetSide(right);
    }

    // 3. The lane index: person nodes by exact y, sorted by x.
    const rowsByY = new Map();
    for (const { node } of persons) {
      let arr = rowsByY.get(node.y);
      if (!arr) rowsByY.set(node.y, (arr = []));
      arr.push(node);
    }
    for (const arr of rowsByY.values()) arr.sort((p, q) => p.x - q.x);
    const rowKeys = [...rowsByY.keys()].sort((p, q) => p - q);
    const lowerBound = (arr, v, key) => {
      let lo = 0;
      let hi = arr.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if ((key ? key(arr[mid]) : arr[mid]) < v) lo = mid + 1;
        else hi = mid;
      }
      return lo;
    };
    const sameRow = (a, b) => Math.abs(a.y - b.y) <= ROW_EPS;
    const gapHasNodeBetween = (aPid, bPid) => {
      const a = nodesById.get(aPid);
      const b = nodesById.get(bPid);
      if (!a || !b || !sameRow(a, b)) return false;
      const lo = Math.min(a.x, b.x);
      const hi = Math.max(a.x, b.x);
      for (
        let ki = lowerBound(rowKeys, a.y - ROW_EPS);
        ki < rowKeys.length && rowKeys[ki] <= a.y + ROW_EPS;
        ki++
      ) {
        const row = rowsByY.get(rowKeys[ki]);
        for (
          let i = lowerBound(row, lo + ROW_EPS, (n) => n.x);
          i < row.length && row[i].x < hi - ROW_EPS;
          i++
        ) {
          const n = row[i];
          if (
            n.pid !== aPid &&
            n.pid !== bPid &&
            Math.abs(n.y - a.y) <= ROW_EPS &&
            n.x > lo + ROW_EPS &&
            n.x < hi - ROW_EPS
          )
            return true;
        }
      }
      return false;
    };
    const bridges = pf.containmentBridgesV3 || new Set();
    const widened = pf.widenedCouplesV3 || new Set();
    const isBridge = (a, b) => bridges.size > 0 && bridges.has(bridgeKey(a, b));
    const isWidened = (a, b) =>
      widened.size > 0 && widened.has(bridgeKey(a, b));
    const bothOnRow = (aPid, bPid) => {
      const a = nodesById.get(aPid);
      const b = nodesById.get(bPid);
      return !!(a && b && sameRow(a, b));
    };
    const needsLane = (aPid, bPid) =>
      bothOnRow(aPid, bPid) &&
      (isBridge(aPid, bPid) ||
        (isWidened(aPid, bPid) && gapHasNodeBetween(aPid, bPid)) ||
        gapHasNodeBetween(aPid, bPid));
    const laneRoutingOn = config.ENABLE_MARRIAGE_LANE_ROUTING !== false;
    const stretchLaneFor = (gen) =>
      (config.STRETCH_MARRIAGE_LANE ?? 0.35) * pf.rowGapBelow(gen);
    const minOff =
      (config.ASYM_JUNCTION_PARENT_CLEARANCE ?? 0.75) *
      (config.SYMBOL_SIZE || 155);
    const stepPx = config.EXTRA_SPOUSE_CHILD_X_STEP ?? 500;
    const asym = pf.asymJunctionsV3;

    // 4. Unions, placed as buildRenderModel places them.
    for (const u of unions) {
      const { person, spouse, shared, node, key } = u;
      const personPos = customPositions?.get(person.uid);
      const spousePos = customPositions?.get(spouse.uid);
      const personNode = nodesById.get(person.pid);
      const spouseNode = nodesById.get(spouse.pid);
      const px = pinX(personPos, rowOf(person)) ?? personNode?.x ?? person.x;
      const py = pinY(personPos) ?? personNode?.y ?? person.y;
      const sx = pinX(spousePos, rowOf(spouse)) ?? spouseNode?.x ?? spouse.x;
      const sy = pinY(spousePos) ?? spouseNode?.y ?? spouse.y;
      const extra = extraSpouseInfo.get(spouse.pid);
      const isAsym = !!asym?.has(key);
      let unionX;
      let unionY;
      if (extra && !isAsym) {
        const dir = sx > px ? 1 : -1;
        const targetX = extra.previousSpouseNode.x + dir * stepPx;
        const lineT = sx !== px ? (targetX - px) / (sx - px) : 0.5;
        const t = Math.max(0.05, Math.min(0.95, lineT));
        unionX = px + t * (sx - px);
        unionY = py + t * (sy - py);
      } else if (isAsym) {
        const xs = shared.map((c) => nodesById.get(c.pid)?.x ?? c.x);
        const targetX = (Math.min(...xs) + Math.max(...xs)) / 2;
        const lineT = sx !== px ? (targetX - px) / (sx - px) : 0.5;
        const span = Math.abs(sx - px);
        const tBand = span > 2 * minOff ? minOff / span : 0.5;
        const t = Math.max(tBand, Math.min(1 - tBand, lineT));
        unionX = px + t * (sx - px);
        unionY = py + t * (sy - py);
      } else {
        unionX = sx + 0.5 * (px - sx);
        unionY = sy + 0.5 * (py - sy);
      }
      let scoreLaneY = null;
      if (needsLane(person.pid, spouse.pid)) {
        const lane = stretchLaneFor(person.virtualY);
        if (laneRoutingOn) unionY = Math.max(py, sy) + lane;
        else scoreLaneY = Math.max(py, sy) + lane;
      }
      node.x = unionX;
      node.y = unionY;
      if (scoreLaneY != null) node.scoreLaneY = scoreLaneY;
      else if ('scoreLaneY' in node) delete node.scoreLaneY;
      if (isAsym) node.asymBand = true;
      else if ('asymBand' in node) delete node.asymBand;
    }

    // 5. Spouse edges take their lane fields.
    for (const { edge, person, spouse } of st.spouseEdges) {
      const fromNode = nodesById.get(person.pid);
      const toNode = nodesById.get(spouse.pid);
      const blocked = needsLane(person.pid, spouse.pid);
      const laneValue = blocked
        ? Math.max(fromNode.y, toNode.y) + stretchLaneFor(person.virtualY)
        : null;
      const laneY = laneRoutingOn ? laneValue : null;
      if (blocked) edge.barBlocked = true;
      else if ('barBlocked' in edge) delete edge.barBlocked;
      if (laneY != null) edge.laneY = laneY;
      else if ('laneY' in edge) delete edge.laneY;
      if (laneY == null && laneValue != null) edge.scoreLaneY = laneValue;
      else if ('scoreLaneY' in edge) delete edge.scoreLaneY;
      if (blocked && isBridge(person.pid, spouse.pid)) edge.laneBridge = true;
      else if ('laneBridge' in edge) delete edge.laneBridge;
    }
  };

  const build = (pf, customPositions = null) => {
    if (
      !state ||
      state.pf !== pf ||
      state.customPositions !== customPositions ||
      state.persons.length !== pf.persons.length
    ) {
      state = initialise(pf, customPositions);
    }
    update(state);
    return {
      nodes: state.nodes,
      edges: state.edges,
      nodesById: state.nodesById
    };
  };

  return { build };
}
